import ImageArrowLeft from "components/utility/images/ImageArrowLeft";
import ImageX from "components/utility/images/ImageX";
import { setGALocation } from "./utility/setGALocation";
import { useRef } from "react";
import {
	getAudienceSelection,
	eventMisfired,
	financialProfessionalGUID,
	individualGUID
} from "./utility/audienceSortApiHandlers";
import { audienceSortStepNames } from "./utility/audienceSortStepNames";
import { AudienceSelectorProps } from "./utility/audienceSortPropTypes";

const AudienceSelector: React.FC<AudienceSelectorProps> = ({
	currentPage,
	audienceSortTheme,
	audienceTypesData,
	countryListData,
	setAudienceSelectionData,
	cameFromInitialStep = false,
	setStep,
	setIsModalOpen
}) => {
	const audienceSortBackButton = useRef<HTMLDivElement>(null);

	if (!countryListData || !audienceTypesData) return;

	const createActiveCountryPersonaLabel = (personaType): string => {
		if (countryListData.regions?.length) {
			const activeCountries: string[] = [];
			countryListData.regions.forEach((region) =>
				region.countries.forEach((country) => {
					if (country.isActive) {
						activeCountries.push(country.title);
					}
				})
			);
			return activeCountries.length ? activeCountries[0] + " | " + personaType : ""; // Will only return first active country from data, API data may be flawed if multiple active countries exist
		}
		return "";
	};

	const closeModal = (event: React.MouseEvent | React.KeyboardEvent) => {
		if (eventMisfired(event)) return;
		if (setIsModalOpen) setIsModalOpen(false);
		setStep(audienceSortStepNames.initialStep);
	};

	const handleBackButton = (event: React.MouseEvent | React.KeyboardEvent): void => {
		if (eventMisfired(event)) return;
		if (cameFromInitialStep) {
			setStep(audienceSortStepNames.notSelecting);
		} else {
			setStep(audienceSortStepNames.countrySelection);
		}
	};

	const handleAudienceSelection = async (
		event: React.MouseEvent | React.KeyboardEvent,
		item
	): Promise<void> => {
		await getAudienceSelection(
			event,
			item,
			"redirect",
			audienceTypesData,
			setAudienceSelectionData,
			currentPage
		);
		if (item.audienceTypeId === financialProfessionalGUID) {
			setStep(audienceSortStepNames.financialProfessionalAffirmation);
		}
	};

	return (
		<div className={`audience-selector-wide-container ${audienceSortTheme}`}>
			<div className="audience-selector-wide-intro">
				<div
					className="audience-selector-wide-title"
					role="button"
					tabIndex={0}
					ref={audienceSortBackButton}
					onClick={handleBackButton}
					onKeyUp={handleBackButton}
					data-qa="audience-selector-wide-title-back-btn"
				>
					<ImageArrowLeft className="persona-back-arrow" />
					{audienceTypesData.locationText ? (
						<div className="audience-selector-wide-intro-label">
							{audienceTypesData.locationText}
						</div>
					) : null}
				</div>

				{currentPage !== "unknown-splash" && (
					<div
						className="audience-selector-wide-close"
						tabIndex={0}
						role="button"
						onKeyUp={closeModal}
						onClick={closeModal}
					>
						<ImageX />
					</div>
				)}
			</div>

			<div className="audience-selector-wide-border">
				<div className="audience-selector-wide-grid">
					<div className="audience-selector-wide-grid-col">
						<div className="audience-selector-wide-grid-first">
							{audienceTypesData.userSelectionText}
						</div>
					</div>
					<div className="audience-selector-wide-grid-col">
						{audienceTypesData.sectionAudienceType.map((item, _index: number, array) => {
							return (
								<div
									key={item.title}
									className={
										"audience-selector-wide-grid" +
										(array.length > 1 ? "-multipleRows" : "-singleRow")
									}
									aria-label={item.title}
									role="button"
									data-a="true"
									data-a-lbl={createActiveCountryPersonaLabel(item.title)}
									data-a-cat={currentPage === "nav" ? "Header" : "Audience"}
									data-a-act={currentPage === "nav" ? "Audience Switcher" : "Audience Selector"}
									data-a-evt={currentPage === "nav" ? "Audience Switch" : "Audience Select"}
									data-a-loc={setGALocation(currentPage)}
									data-a-text={item.title}
									data-a-comp="audience switcher modal"
									tabIndex={0}
									onClick={async (event) => {
										handleAudienceSelection(event, item);
									}}
									onKeyUp={async (event) => {
										if (eventMisfired(event)) return;
										handleAudienceSelection(event, item);
									}}
									data-qa={
										(item.audienceTypeId === financialProfessionalGUID &&
											"audience-button-professional") ||
										(item.audienceTypeId === individualGUID && "audience-button-individual")
									}
								>
									<div className="audience-selector-wide-grid-row-title">{item.title}</div>
								</div>
							);
						})}
					</div>
					<div className="audience-selector-wide-grid-col" />
				</div>
			</div>
		</div>
	);
};

export default AudienceSelector;
